import { useSuspenseInfiniteQuery, useQueryClient } from '@tanstack/react-query'

import { getRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'
import type { GetTimelineOptions, TimelineResponse, StreamPost } from 'src/shared/schemas/stream/feed'

const getUserFeed = async (userId: number, isPublic: boolean, options?: GetTimelineOptions) => {
  try {
    const queryParams = new URLSearchParams()
    queryParams.append('user_id', userId.toString())
    if (options?.offset !== undefined) queryParams.append('offset', options.offset.toString())
    if (options?.limit !== undefined) queryParams.append('limit', options.limit.toString())

    const response = await getRequest<TimelineResponse>(
      `/feeds/${isPublic ? 'public-user-feed' : 'user-feed'}?${queryParams.toString()}`,
    )

    if (!response) {
      throw new Error('Failed to fetch user feed')
    }
    return response
  } catch (error) {
    throw new Error(`Failed to fetch user feed | ${error}`)
  }
}

export const useUserFeed = (userId: number, isPublic = false, options?: Omit<GetTimelineOptions, 'offset'>) => {
  const queryClient = useQueryClient()

  return useSuspenseInfiniteQuery({
    ...queries.feeds.userFeed(userId, isPublic),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await getUserFeed(userId, isPublic, { ...options, limit: 5, offset: pageParam })

      response.results.forEach((post: StreamPost) => {
        queryClient.setQueryData(queries.feeds.post(post.id).queryKey, post)
      })

      return response
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    retry: 1,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}
